import React, { useState, useEffect } from 'react';
import { gdprAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'restaflex_cookie_consent';

function generateSessionId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function CookieConsentBanner({ restaurantId, accent }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    }
  }, []);

  async function saveConsent(analyticsVal, marketingVal) {
    let sessionId = localStorage.getItem('restaflex_session_id');
    if (!sessionId) {
      sessionId = generateSessionId();
      localStorage.setItem('restaflex_session_id', sessionId);
    }

    const consent = { necessary: true, analytics: analyticsVal, marketing: marketingVal };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));

    try {
      await gdprAPI.saveCookieConsent({
        session_id: sessionId,
        analytics: analyticsVal ? 1 : 0,
        marketing: marketingVal ? 1 : 0,
        restaurant_id: restaurantId || null,
      });
    } catch {
      // Sessiz başarısızlık — çerez rızası UI'ı engellememeli
    }

    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label={t('cookieBannerTitle') || 'Çerez bildirimi'}
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-xl"
      style={{ fontFamily: 'var(--qm-font, inherit)' }}>
      <div className="max-w-4xl mx-auto">
        {!showDetails ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{t('cookieBannerTitle')}</strong> {t('cookieBannerDesc')}{' '}
                <button onClick={() => setShowDetails(true)} aria-label={t('cookieBannerManage')} className="text-blue-600 underline text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">
                  {t('cookieBannerManage')}
                </button>
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => saveConsent(false, false)}
                aria-label={t('cookieBannerOnlyRequired')}
                className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                {t('cookieBannerOnlyRequired')}
              </button>
              <button onClick={() => saveConsent(true, true)}
                aria-label={t('cookieBannerAcceptAll')}
                className="text-white px-3 py-1.5 rounded text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-opacity hover:opacity-90"
                style={accent ? { background: accent } : undefined}
              >
                {t('cookieBannerAcceptAll')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('cookieBannerDetailsTitle')}</h3>
            <div className="space-y-2">
              <label className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{t('cookieCategoryRequired')}</div>
                  <div className="text-xs text-gray-500">{t('cookieCategoryRequiredDesc')}</div>
                </div>
                <input type="checkbox" checked disabled className="rounded" />
              </label>
              <label className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{t('cookieCategoryAnalytics')}</div>
                  <div className="text-xs text-gray-500">{t('cookieCategoryAnalyticsDesc')}</div>
                </div>
                <input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} className="rounded" />
              </label>
              <label className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{t('cookieCategoryMarketing')}</div>
                  <div className="text-xs text-gray-500">{t('cookieCategoryMarketingDesc')}</div>
                </div>
                <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} className="rounded" />
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => saveConsent(analytics, marketing)}
                aria-label={t('cookieBannerSaveChoices')}
                className="text-white px-4 py-2 rounded text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-opacity hover:opacity-90"
                style={accent ? { background: accent } : { background: '#2563eb' }}
              >
                {t('cookieBannerSaveChoices')}
              </button>
              <button onClick={() => saveConsent(true, true)}
                aria-label={t('cookieBannerAcceptAll')}
                className="text-white px-4 py-2 rounded text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-opacity hover:opacity-90"
                style={accent ? { background: accent, filter: 'brightness(0.92)' } : { background: '#16a34a' }}
              >
                {t('cookieBannerAcceptAll')}
              </button>
              <button onClick={() => setShowDetails(false)}
                aria-label={t('back')}
                className="border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-4 py-2 rounded text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                {t('back')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
